// Server-side merge-field resolver + safelisted catalog for 1:1 /
// automation emails.
//
// CONSUMER: internal-ui (Workers runtime + tests)
//
// Resolves {{contact.preferred_name}} / {{household.city}} /
// {{transaction.stage}} / similar tokens to actual values from the
// in-flight context. The composer/builder use this to preview
// substitutions client-side AND the send route uses the same resolver
// server-side so what hits Graph matches what the user reviewed.
//
// ── SENSITIVE-FIELD GUARDRAIL (non-negotiable) ──────────────────────
// No encrypted / regulated field may EVER be a merge token — no SSN
// (self / spouse / co-borrower), driver's license, bank account /
// routing / name, Medicare id, or any `*_encrypted` / `*_last_four`
// column. This is enforced STRUCTURALLY, the same way
// shared/filters/field-registry.ts excludes them:
//   1. Every catalog entry is filtered through isSensitiveTokenName()
//      before it is exported — a token whose name trips the denylist is
//      dropped from the catalog, so it can never be offered in the
//      picker and is never a "known" token the resolver will fill.
//   2. tests/db/merge-fields-catalog.test.ts fails if any exported token
//      matches the denylist, and asserts the denylist rejects the known
//      sensitive names. The context builder (merge-context-builder.ts)
//      independently maps ONLY safe-projection columns into the context,
//      so a sensitive value is never present to leak even if a token
//      name somehow slipped through.

// ─── Merge context shape ────────────────────────────────────────────
//
// Every value is a display string (or null/absent). The context builder
// does all presentation formatting (dates → "March 3, 2027", money →
// "$350,000", fit → "Ideal Fit"), so the resolver stays a pure string
// substitution + HTML-escape. Only ONE of transaction / opportunity /
// service_record is ever populated on a given send — the one matching
// the rule's grain (the "linked record"). On a contact- or household-
// grain rule none are populated, so those tokens resolve to blank
// rather than leaking another record's data.

import { OFFICE_EMAIL, OFFICE_PHONE_DISPLAY } from "../forms/office-contact";
import { OFFER_SLOTS } from "../realty/offer-slots";
import { PRODUCTIONS_SLUG } from "../esign/productions-access";

export interface MergeContext {
  contact?: {
    preferred_name?: string | null;
    legal_first_name?: string | null;
    legal_last_name?: string | null;
    full_name?: string | null;
    /** Legal first + legal last name ONLY — never a nickname
     *  substitute. See the catalog entry below for why this exists
     *  as a field distinct from full_name. */
    legal_full_name?: string | null;
    email?: string | null;
    phone?: string | null;
    birthday?: string | null;
    referral_source?: string | null;
    fit_assessment?: string | null;
  };
  household?: {
    display_name?: string | null;
    primary_first_name?: string | null;
    primary_last_name?: string | null;
    city?: string | null;
    state?: string | null;
    /** Full one-line mailing address ("5522 E Azara Dr, San Tan Valley,
     *  AZ 85140"), resolved via loadHouseholdMailingAddress — the same
     *  household-row-else-contact-fallback the household page's Mailing
     *  Address card uses, so a household with no street on its own row
     *  but an address on a member contact still merges correctly. */
    mailing_address?: string | null;
    aum?: string | null;
    client_since?: string | null;
  };
  advisor?: {
    name?: string | null;
    email?: string | null;
    /**
     * Real-estate license number + state (2026-08-29), from `licenses`
     * (license_type='real_estate'). The AAR Purchase Contract's own
     * broker/license block on nearly every document — the sending
     * agent's license, retyped by hand today. Not company-gated the
     * way transaction/service_record tokens are (advisor.* has never
     * been gated per-grain) — harmless to resolve everywhere, just
     * irrelevant outside Realty, so it's `realtyOnly` for the PICKER
     * only, same reasoning as the other Realty-specific tokens.
     *
     * Firm-level licence is a DIFFERENT fact, and as of 2026-09-16 it
     * has its own home: `firm.license_number`, off `firm_profiles`.
     * This one is only ever the sending agent's own.
     */
    license_number?: string | null;
    license_state?: string | null;
    /** The agent's own MLS code (2026-09-16), beside the licence number
     *  the table already held. Nate ns471, Mark ms432. */
    mls_code?: string | null;
    /** The number they want PRINTED on a contract, which is not always
     *  the one in their user profile. */
    contract_phone?: string | null;
  };
  /**
   * The agent across the table, off the party roster (2026-09-16).
   *
   * On a purchase this is the listing agent; on a listing it is the
   * buyer's agent. Absent when the deal's side is unknown, on purpose —
   * a guess here prints a stranger's name, or our own, on the wrong
   * signature line.
   *
   * Business credentials only. The other side's CLIENT is never here:
   * their details are typed from the title report, and the MLS sheet
   * reader refuses to carry them at all.
   */
  other_agent?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    license_number?: string | null;
    mls_code?: string | null;
    firm_name?: string | null;
    firm_mls_code?: string | null;
    firm_license_number?: string | null;
  };
  /**
   * The firm's own block (2026-09-16): who the broker is, as it prints
   * near the signatures on every AAR contract.
   *
   * Resolved from the COMPANY OF THE RECORD being sent, not from the
   * sender: a Realty contract prints Stewardship Realty's block whoever
   * pressed send. Absent when the caller does not say which company, and
   * blank for a company whose row nobody has filled in — both of which
   * print exactly what prints today, which is nothing.
   */
  firm?: {
    name?: string | null;
    street?: string | null;
    /** Street and city on one line — AAR line 446 prints them together
     *  and gives STATE and ZIP their own boxes beside it. */
    street_and_city?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    phone?: string | null;
    fax?: string | null;
    email?: string | null;
    license_number?: string | null;
    industry_code?: string | null;
  };
  /** The firm's standing answers on a purchase contract
   *  (realty_offer_defaults). Every one is optional and resolves BLANK
   *  when nobody has answered it — five of the ten columns are
   *  deliberately unanswered, and a blank there is a question for the
   *  agent rather than a term for the client. */
  offer_default?: {
    earnest_money_form?: string | null;
    seller_response_days?: string | null;
    receipts_days_prior_coe?: string | null;
    buyer_broker_geographic_area?: string | null;
    home_warranty_placeholder?: string | null;
  };
  // The "linked record" — populated ONLY when the rule's grain is that
  // workflow entity. See merge-context-builder.ts.
  transaction?: MergeRecordFields;
  opportunity?: MergeRecordFields;
  service_record?: MergeServiceRecordFields;
  /**
   * The terms of an offer, as the advisor accepted them off their own
   * spoken summary (2026-09-01 — Nate's offer summary box).
   *
   * A namespace of its own rather than a few more `opportunity.*`
   * tokens, because these are not facts ABOUT an opportunity — they are
   * the terms of a specific offer written from it.
   *
   * WHY IT ISN'T `transaction.*`. The OS already has a governed home
   * for negotiated terms (earnest money, possession, concessions) on
   * `transactions`, written by the Realty contract card. An offer has
   * no transaction: Processing is created when an offer is ACCEPTED,
   * not when one is drafted. So an offer's terms need a home of their
   * own until acceptance, and this is that namespace.
   *
   * Keyed by slot key from shared/realty/offer-slots.ts. Every value in
   * here was accepted by a human — nothing a model proposed reaches
   * this context without that step.
   */
  offer?: Record<string, string | null>;
  // Ongoing Care. Populated on a care-plan-grain rule — which ALSO
  // populates service_record from the plan's linked policy/loan/case, so
  // an email fired by a care plan can name the policy it is about (Grant,
  // 2026-08-12: a care-plan rule offered no way to merge the policy
  // number or carrier).
  care_plan?: MergeCarePlanFields;
  // Populated ONLY by automation-engine.ts's executeEmailAction, NOT by
  // buildMergeContext — minting a link is a database write (a fresh
  // intake_form_instance row), not a pure lookup, and only happens when
  // the content actually references the token (so a rule that never
  // mentions the form never creates unused instances). See the
  // "form.renewal_intake_link" handling in automation-engine.ts.
  form?: {
    renewal_intake_link?: string | null;
    /** The generic one: a personal link to whichever form the rule
     *  names in its `form_slug` config. Same side-effecting contract. */
    link?: string | null;
    /** Short clause naming who on a RETURNED questionnaire still needs a
     *  human, or "" when nobody does — the only form token that is a pure
     *  read of the triggering event rather than a mint. Overlaid by the
     *  engine, never built here. */
    follow_up_note?: string | null;
  };
  // Same engine-only, side-effecting contract as `form` above: the
  // personal booking link mints a fresh booking_invite_tokens row for
  // the RECIPIENT at send time (native scheduling Phase C), so a
  // renewal email can carry a zero-typing "grab a time with me" link.
  // Never populated by buildMergeContext. See "booking.link" handling
  // in automation-engine.ts.
  booking?: {
    link?: string | null;
  };
  // Engine-only like `booking`, but a pure READ (no side effect): the
  // recipient's next upcoming booked appointment — when, with whom,
  // what type — resolved at send time so a reminder can say "Thursday
  // at 2:00 PM with Joy" (native scheduling Phase C2). Never populated
  // by buildMergeContext. See "appointment.*" handling in
  // automation-engine.ts.
  appointment?: {
    when?: string | null;
    advisor?: string | null;
    type?: string | null;
    /** Fresh-per-reminder reschedule/cancel link (a mint side effect,
     *  unlike the read-only fields above). */
    manage_link?: string | null;
  };

  // Win-back (2026-09-23). Never populated by buildMergeContext: the
  // engine overlays it from the scan row when a rule fires on "the county
  // shows a refinance with another lender" (shared/mortgage/county-merge.ts).
  // Absent on every other trigger, so the tokens render empty there.
  county?: {
    new_lender?: string | null;
    recorded_month?: string | null;
    new_amount?: string | null;
    new_loan_type?: string | null;
    as_of?: string | null;
    our_amount?: string | null;
    our_funded_year?: string | null;
    other_services?: string | null;
  };
  // The document a signer just signed (2026-08-23). Never populated by
  // buildMergeContext — it comes from the "a document is signed" event's
  // payload. See "document.*" handling in automation-engine.ts.
  document?: {
    name?: string | null;
    /** A fresh copy link minted for this recipient (a side effect, like
     *  booking.link) — the one they already hold keeps working. */
    link?: string | null;
  };

  /** THE SEND ITSELF (Baker, 2026-09-16).
   *
   *  Not the record and not the signer: the envelope. Baker's Affiliated
   *  Business Arrangement Disclosure has a "Date" line at the top that
   *  should carry the day the package went out, and there was nothing
   *  anywhere to bind it to — `document.*` is the SIGNED copy, filled
   *  only by the "a document is signed" event, and nothing described
   *  the outgoing send.
   *
   *  Its own group rather than a new `document.` token, deliberately:
   *  putting it there would have meant offering `document.name` and
   *  `document.link` on the send screen too, and both resolve to
   *  nothing at send time. A picker that offers two blanks to get one
   *  real field is a worse trade than a new word. */
  envelope?: {
    /** The day this package is sent, in Arizona time. */
    date_sent?: string | null;
    /** Six months after date_sent (the Buyer-Broker agreement's term). */
    date_sent_plus_six_months?: string | null;
  };

  /** THE PERSON SIGNING THIS BOX, as the send names them (Baker,
   *  2026-10-03).
   *
   *  Never built by buildMergeContext: it differs per box, so the
   *  e-signature send fills it per signer from the envelope's own signer
   *  list (resolveFieldBindingsPerSigner in shared/esign/field-merge-
   *  bindings.ts). Exists because a team member, a new hire or a co-op
   *  agent is not a contact, so `contact.*` has nothing to read for
   *  them, and their name on the send is the one name they do have. */
  signer?: {
    name?: string | null;
  };
}

/** Common workflow-record token fields (transaction / opportunity). */
export interface MergeRecordFields {
  type?: string | null;
  stage?: string | null;
  product_noun?: string | null;
  amount?: string | null;
  revenue?: string | null;
  expected_close?: string | null;
  /** Realty only: "Day 4 of 10 · ends August 15, 2026". Blank unless the
   *  record carries BOTH the inspection end date and the period length. */
  inspection_period?: string | null;
  /**
   * The negotiated terms of a Realty deal — Nate Swartz's contract card
   * (migration 20260825163218), the one place these facts live. Every
   * one is a real blank on the AAR Purchase Contract (§1c earnest money,
   * §1e possession, §2h financing type, §2j seller concessions) that
   * still gets retyped by hand. `transactions`-only: `opportunities`
   * never populates these (no columns exist there), so they resolve
   * blank on every non-Realty and every Opportunity-grain send.
   */
  earnest_money?: string | null;
  loan_type?: string | null;
  loan_amount?: string | null;
  possession?: string | null;
  seller_concessions?: string | null;
  repair_credit?: string | null;
  /** Commission credit at closing (2026-10-27): a bare number ("1,500.00",
   *  "0.00" when none) and who it goes to ("Buyer", "Seller", "N/A"). */
  commission_credit_amount?: string | null;
  commission_credit_to?: string | null;
  program_id?: string | null;
  cda_sent_to_title?: string | null;
  /** The day the BUYER signed the purchase contract (2026-10-14; AAR
   *  line 452) — the date many AAR forms call the contract "dated". */
  contract_dated?: string | null;
  /**
   * The other side's name as it prints on the contract (Grant,
   * 2026-09-03) — a COURTESY field, and the distinction is the point:
   * on a buy-side deal the seller signs the other agent's copy, never
   * ours, so this name is merged onto the form and its owner is never
   * a signer, never emailed, and never a contact in our database.
   */
  other_party_name?: string | null;
  /**
   * The deal's escrow/title contact (2026-08-29) — the AAR Purchase
   * Contract §3a block (escrow company name/address/phone/email/fax).
   * Only name and email have a home in the OS today (`vendors` /
   * `transaction_parties` carry neither an address nor a fax column);
   * this fills the two fields that genuinely exist rather than
   * inventing the rest. Read off the same 'title'/'escrow' party-role
   * lookup, with the same title-preferred-over-escrow order, that
   * shared/realty/transaction-messages.ts's draft-recipient logic
   * already uses — one lookup, one answer, not a second opinion.
   */
  escrow_company_name?: string | null;
  escrow_company_email?: string | null;
  /** The PERSON at the title company, off the same roster row (the
   *  contacts tab's escrow/title party), and the title company's file
   *  number for the deal (realty_*.escrow_number). Nate, 2026-10-26: both
   *  are asked for by the Commission Disbursement form. */
  escrow_officer_name?: string | null;
  escrow_file_number?: string | null;
  /** The title company's address in the pieces §3a prints it in, and its
   *  phone (Nate, 2026-11-03: "this is title officer info that was
   *  provided when drafting the forms but it has not been able to be
   *  copied"). On an offer, the title officer Step 1 of Draft an Offer
   *  names; on a Processing record, the escrow/title party's vendor.
   *  The address is one typed line in both homes, split by
   *  shared/realty/escrow-block.ts, which leaves a piece blank rather
   *  than guess it. Fax has no home and stays typed. */
  escrow_company_street?: string | null;
  escrow_company_city?: string | null;
  escrow_company_state?: string | null;
  escrow_company_zip?: string | null;
  escrow_company_phone?: string | null;
  /**
   * Who this record is FOR — "Sam and Sarah Sample" on an estate plan
   * both spouses are named on, "Sam Sample" on a Medicare case that only
   * one household member is named on (Andrea's thank-you-note ask,
   * 2026-08-30: the household has an address token, but nothing said
   * WHOSE name goes on the card). Reads the record's own named people
   * (migration 0252's opportunity_contacts / transaction_contacts — the
   * transaction grain defers to a linked service record's people, same
   * precedence resolveTransactionPeople already encodes for the UI).
   * Null when the record names nobody yet. Carries a trailing
   * "(unconfirmed — check the record)" when every name on it is a
   * system guess rather than someone's own confirmation — see
   * formatRecordPeople in merge-context-builder.ts.
   */
  people?: string | null;
  /** Every person the record is for, by LEGAL name, one line — see the
   *  catalog entry. Null when anyone on it lacks a legal first or last
   *  name, so the line is never printed missing a buyer. */
  people_legal_names?: string | null;
  /** The expected close date in the separate boxes a form splits it
   *  across (shared/realty/date-parts.ts). */
  expected_close_month?: string | null;
  expected_close_day?: string | null;
  expected_close_year_short?: string | null;
}

/** Ongoing Care (care plan) token fields. */
export interface MergeCarePlanFields {
  /** The advisor-facing plan name ("Renewal Plan"). */
  name?: string | null;
  /** Where the household is in the plan ("Stay Recommended"). */
  stage?: string | null;
  cycle_start?: string | null;
  cycle_end?: string | null;
}

/** Service-record token fields (adds carrier / policy for insurance). */
export interface MergeServiceRecordFields {
  type?: string | null;
  product_noun?: string | null;
  status?: string | null;
  effective_date?: string | null;
  renewal_date?: string | null;
  amount?: string | null;
  carrier?: string | null;
  policy_number?: string | null;
  /** Mortgage loans. */
  loan_number?: string | null;
  /** Medicare / health Planning cases only — the same short label
   *  (medicareTierLabel) the Ongoing Care renewal board already shows
   *  Ryan and Andrea ("MA/MAPD", "PDP", "Medigap G"). Null for every
   *  other service-record family and for a Medicare case with no tier
   *  recorded yet. */
  plan_type?: string | null;
  /** Realty only — the per-side commission rate the client agrees to,
   *  and the total commission on the deal. What a buyer-broker agreement
   *  and a purchase contract disclose; see MergeTokenDef.realtyOnly. */
  commission_rate?: string | null;
  gross_commission?: string | null;
  /** Realty listings only (Nate, 2026-10-26): our listing-side commission
   *  as the two numbers a disbursement form prints in separate boxes. The
   *  rate is a BARE number ("1.98") and the amount carries no "$"
   *  ("10,890.00"), because the form prints the % and $ itself. */
  listing_commission_pct?: string | null;
  listing_commission_amount?: string | null;
  /** Realty (2026-10-27): the buyer's agent's commission in the same two
   *  bare-number boxes. On a listing, what we offer the buyer's agent; on a
   *  purchase, what we earn as that agent. */
  buyer_agent_commission_pct?: string | null;
  buyer_agent_commission_amount?: string | null;
  /** The property, one line, from whichever subtype carries one — a
   *  loan, a listing, or a purchase (2026-08-23, for documents: a real
   *  estate or mortgage contract is ABOUT the property). */
  property_address?: string | null;
  /** The same address in the pieces a contract prints in separate boxes
   *  (Nate, 2026-10-15: the AAR Purchase Contract's line 5-6 is
   *  "Premises Address", then "City", "County", "AZ, Zip Code"). Read
   *  from the same row as `property_address`, so the two never
   *  disagree; absent wherever the whole address is absent. */
  property_street?: string | null;
  property_city?: string | null;
  property_zip?: string | null;
  /** The property's legal identity, off realty_property_facts +
   *  year_built (2026-08-29). What a contract names when "the address"
   *  is not precise enough to convey land.
   *
   *  Three-state, and the distinction is the point:
   *    null  — this IS a property record and the fact is unanswered, so
   *            the send guard stops the document and names the blank;
   *    ""    — no property on this record at all (a policy, a portfolio),
   *            so the token is inapplicable and simply renders empty;
   *    value — the fact, as it goes on the form. */
  parcel_number?: string | null;
  county?: string | null;
  legal_description?: string | null;
  year_built?: string | null;
  /** The representation agreement's own dates — Listing Contract §3 on a
   *  listing, Buyer-Broker Agreement on a purchase. */
  agreement_signed_date?: string | null;
  agreement_expiration_date?: string | null;
  /** The listing agreement's own terms (2026-10-14). Listing side only:
   *  "" on a purchase or a non-property record (inapplicable), null on a
   *  listing where nobody has filled it in (a hole a bound send stops on). */
  listing_period_start_date?: string | null;
  original_list_price?: string | null;
  /** "None" when the agent answered no; the detail line (or "Yes") when yes. */
  leased_items?: string | null;
  items_not_conveying?: string | null;
  /** Listing Contract §4's additional compensation when our own agent
   *  finds the buyer. Firm policy (realty_listing_contract_defaults),
   *  not a per-listing column — see the catalog entry. */
  unrepresented_buyer_rate?: string | null;
  /** Who this record is FOR — see MergeRecordFields.people (same field,
   *  same formatting, same unconfirmed marker). On a care-plan grain
   *  this is the linked policy/loan/case's people, not the care plan
   *  itself (Ongoing Care has no people of its own). */
  people?: string | null;
}

const TOKEN_PATTERN = /\{\{\s*([\w.]+)\s*\}\}/g;

function lookup(context: MergeContext, path: string): string | null {
  const parts = path.split(".");
  let cur: unknown = context;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in cur) {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      return null;
    }
  }
  if (typeof cur === "string") return cur;
  return null;
}

/** True when the token's top-level namespace object (contact / household
 *  / advisor / transaction / opportunity / service_record) is present in
 *  the context — i.e. the grain HAS this kind of record, so an unresolved
 *  leaf is a data hole (keep it literal for the send-path guard), not an
 *  inapplicable token (blank it). */
function namespacePresent(context: MergeContext, path: string): boolean {
  const ns = path.split(".")[0];
  if (!ns) return false;
  const val = (context as Record<string, unknown>)[ns];
  return typeof val === "object" && val !== null;
}

/** Escape a resolved merge VALUE for an HTML context. The template's own
 *  markup is trusted, but a merged value (a contact name, a campaign
 *  recipient's first name) must never inject tags into an outgoing email.
 *  Exported so every HTML email path — 1:1, automation, AND bulk (SES) —
 *  reuses ONE escape helper instead of drifting copies. */
export function escapeHtmlValue(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** http/https only. A resolved link value is ours (we minted it moments
 *  ago), but this is the one place a merged value becomes an href, and an
 *  href built from data deserves the same scheme check the rich-text
 *  sanitizer applies to authored markup. Anything else falls back to
 *  rendering the value as plain text — visible and harmless. */
function isSafeLinkUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

// The brand CTA button, inline-styled so it survives inside an actual
// email — Graph sends this HTML with no <head>/<style> block, so a CSS
// class (which is all the in-app preview relies on) does nothing here.
// background-color is the fallback Outlook desktop's Word-based renderer
// falls back to when it ignores background-image/box-shadow; every other
// client (Gmail, Apple Mail, Outlook web/mobile) renders the gradient.
// Values are pinned to the in-app preview's `.email-cfg__cta` class
// (web/src/styles.css) so what a rule owner approves in the builder is
// what actually lands as a button in the recipient's inbox — until
// 2026-09-08 this function emitted a bare <a>, so the preview showed a
// button but Outlook rendered a plain blue link.
const CTA_BUTTON_STYLE =
  "display:inline-block;margin:2px 0;" +
  "background-color:#4e98c6;" +
  "background-image:linear-gradient(180deg,#5ba3d0 0%,#4e98c6 55%,#3a7ea8 100%);" +
  "color:#ffffff;text-decoration:none;" +
  "font-family:Montserrat,Arial,Helvetica,sans-serif;font-weight:800;" +
  "font-size:11px;letter-spacing:0.1em;text-transform:uppercase;" +
  "padding:8px 14px;border-radius:6px;box-shadow:0 3px 10px rgba(2,44,81,0.25);";

/**
 * Render one resolved link token as the brand CTA button.
 *
 * Both halves are escaped: the URL because it lands in an attribute, the
 * label because an author types it. The surrounding tags are ours, not
 * the author's, which is why this is safe to emit into a body that is
 * otherwise fully escaped.
 *
 * Exported for tests.
 */
export function renderMergeLinkAnchor(url: string, linkText: string): string {
  const label = linkText.trim() || url;
  return (
    `<a href="${escapeHtmlValue(url)}" target="_blank" rel="noopener noreferrer" ` +
    `style="${CTA_BUTTON_STYLE}">${escapeHtmlValue(label)}</a>`
  );
}

export function applyMergeFields(
  template: string,
  context: MergeContext,
  opts?: {
    /** Escape resolved VALUES for HTML contexts — the template's own
     *  markup is trusted, but a merged contact name must never inject
     *  tags into an outgoing email (audit fix 2026-07-03). */
    escapeHtmlValues?: boolean;
    /** Render URL-valued tokens (see MergeTokenDef.isLink) as real
     *  anchors, keyed token path -> the words to show. HTML contexts
     *  only: the text/plain twin of a body wants the bare address, which
     *  is what omitting this gives.
     *
     *  A token absent from this map renders as its plain value, so a
     *  caller can enable anchors for the form link without silently
     *  turning every other URL in the body into markup. */
    linkText?: Readonly<Record<string, string>>;
  },
): string {
  if (!template) return template;
  return template.replace(TOKEN_PATTERN, (match, path) => {
    const resolved = lookup(context, path);
    if (resolved !== null) {
      const linkText = opts?.linkText?.[path];
      if (opts?.escapeHtmlValues && linkText !== undefined && isSafeLinkUrl(resolved)) {
        return renderMergeLinkAnchor(resolved, linkText);
      }
      return opts?.escapeHtmlValues ? escapeHtmlValue(resolved) : resolved;
    }
    // Unresolved — three cases:
    //  1. UNKNOWN token (a typo, or a deliberately-excluded sensitive
    //     name) → left as typed so the author notices it did nothing, and
    //     the send-path guard blocks an auto-send carrying it.
    //  2. KNOWN token whose whole namespace is ABSENT — the rule's grain
    //     genuinely has no such record (e.g. {{opportunity.stage}} on a
    //     transaction rule) → renders EMPTY. Never the literal {{token}}
    //     in a client's inbox, and never another record's value.
    //  3. KNOWN token whose namespace IS present but this leaf is empty
    //     (a missing first name, a household with no city) → left as typed
    //     so the engine's unresolved-merge-fields guard still blocks the
    //     auto-send (a human fills the hole from the draft). A present-but-
    //     empty personalization field is a data hole, not an inapplicable
    //     token.
    if (!KNOWN_TOKEN_SET.has(path)) return match;
    return namespacePresent(context, path) ? match : "";
  });
}

/** Every {{token}} path referenced in a piece of text (deduped). Used by
 *  the context builder to skip loading a namespace no token references. */
export function extractMergeTokens(...texts: (string | null | undefined)[]): Set<string> {
  const out = new Set<string>();
  for (const t of texts) {
    if (!t) continue;
    for (const m of t.matchAll(TOKEN_PATTERN)) {
      if (m[1]) out.add(m[1]);
    }
  }
  return out;
}

// ─── The safelisted catalog ─────────────────────────────────────────

export type MergeTokenGroup =
  | "contact"
  | "household"
  | "record"
  | "form"
  | "booking"
  | "appointment"
  // The document a client just signed — fillable only on the
  // "a document is signed" trigger (2026-08-23).
  | "document"
  // What the county recorder shows about a house we financed (win-back,
  // 2026-09-23), fillable only on "the county shows a refinance with
  // another lender", from that scan row's own facts. Offered only on
  // service-record rules (email-editor-helpers.ts).
  | "county"
  // The outgoing send itself (2026-09-16) — offered only by the
  // e-signature picker, where it is the one place it means anything.
  | "envelope"
  // The person signing a box, by the name on the send (2026-10-03) —
  // offered only by the e-signature picker, like "envelope".
  | "signer"
  // The sending advisor's own credentials (2026-08-29) — currently
  // just the real-estate license, so it's Realty-only and offered ONLY
  // by the e-signature picker (see esign.ts's OFFERED_GROUPS), never
  // the automation email editor. Distinct from advisor.name/email,
  // which resolve everywhere but are deliberately NOT offered as
  // pickable chips (they already appear in the sender line/signature —
  // see ADVISOR_TOKENS below).
  | "advisor"
  // The firm's own block (2026-09-16) — the broker identity every AAR
  // contract asks for near the signatures. One governed row per company
  // (`firm_profiles`), so unlike `advisor` this is NOT Realty-only: the
  // Loan Status Update asks Mortgage the same questions in NMLS words.
  | "firm"
  | "offer_default"
  // The agent ACROSS THE TABLE (2026-09-16) — the other brokerage on a
  // Realty deal. Which role that is depends on which side of the deal
  // the record is; see shared/realty/agent-side-resolution.ts.
  | "other_agent";

/** Which workflow grain a "record" token belongs to — used by the
 *  picker to show only the tokens matching the rule's grain. */
export type MergeRecordGrain =
  | "transaction"
  | "opportunity"
  | "service_record"
  | "rhythm_instance";

/**
 * Which record-token grains a rule of this grain can actually fill.
 *
 * Usually just itself. The exception is Ongoing Care: a care plan is
 * attached to a policy / loan / case (`linked_service_record_id`), so a
 * care-plan rule can name that record too — the context builder resolves
 * both. Grant, 2026-08-12: an email drafted by a care-plan stage move
 * offered no way to merge "policy number or carrier", because the picker
 * matched the grain exactly and no token was tagged `rhythm_instance`.
 *
 * A household-level care plan (no linked record) leaves the
 * service_record namespace absent, so those tokens render blank rather
 * than as literal {{braces}} in a client's inbox.
 */
export function recordGrainsFor(
  grain: MergeRecordGrain | null,
): readonly MergeRecordGrain[] {
  if (grain === null) return [];
  if (grain === "rhythm_instance") return ["rhythm_instance", "service_record"];
  return [grain];
}

export interface MergeTokenDef {
  /** Bare token path, e.g. "household.city". */
  token: string;
  group: MergeTokenGroup;
  /** Plain-English label shown on the insert chip. */
  label: string;
  /** Value substituted in the client-side sample preview. */
  sample: string;
  /** For group="record" only: the grain this token applies to. */
  grain?: MergeRecordGrain;
  /** INTERNAL_ONLY — this value must never reach a client, so it is
   *  stripped from the exported catalog and can be offered nowhere.
   *
   *  Distinct from the sensitive denylist below, which is about
   *  REGULATED data (SSN, bank, card). This list is about data that is
   *  merely OURS: what we privately think of a household, and what we
   *  earn from them. CLAUDE.md names both directly — "What clients will
   *  NEVER see: Fit Assessment values … Commission data or deal values."
   *
   *  Marked rather than deleted so the reason travels with the token and
   *  a later reader cannot "restore" it as an oversight.
   *
   *  Deliberately absolute rather than audience-aware (2026-08-23). An
   *  internal teammate email arguably could carry these, but the editor
   *  that composes client mail and the one that composes teammate mail
   *  are the same component, so an audience flag would be exactly the
   *  "we'll remember to hide it" mechanism this codebase rejects — the
   *  client-portal section makes the same argument for structuring
   *  privacy rather than assuming it. A teammate who needs the fit
   *  assessment opens the record. */
  internalOnly?: boolean;
  /** Disclosable on REALTY records only, internal everywhere else.
   *
   *  Arizona real-estate practice requires the broker's compensation to
   *  be stated on buyer-broker agreements and purchase contracts (Grant,
   *  2026-08-23: "Real estate law requires us to communicate how much
   *  money we make on transactions and put them on buyer broker
   *  agreements and real estate contracts"). So on a Realty document the
   *  commission is not a leak — it is the point of the paperwork.
   *
   *  It stays internal for the other four companies, where no such duty
   *  exists and printing it on a client's policy or loan paperwork would
   *  be the leak CLAUDE.md forbids.
   *
   *  Kept OUT of the static catalog because that catalog is company-blind
   *  — the automation email editor reads it without knowing whose record
   *  a rule will fire on. The e-signature picker, which does know the
   *  record's company, adds these back for Realty. Disclosure belongs on
   *  the contract, not in a marketing email; flagged for Grant. */
  realtyOnly?: boolean;
  /** This token resolves to a URL, so an HTML email should render it as a
   *  real clickable link rather than as the raw address in body text.
   *
   *  Andrea, 2026-09-03: every OEP draft arrived with a 60-character
   *  /f/t/<token> URL sitting in the prose as plain text, and she
   *  hand-linked all 100 of them before sending. Nothing in the pipeline
   *  was wrong in isolation — the token resolved, the body escaped
   *  correctly — but no step ever knew the resolved VALUE was a URL, so
   *  the address was the only thing a client could be shown.
   *
   *  Marking it on the token (rather than sniffing "does this look like a
   *  URL?" at render time) keeps the decision declarative: a token is a
   *  link because its definition says so, and a contact whose company
   *  name happens to start with "https://" is still just text. */
  isLink?: boolean;
  /** Anchor text when the author hasn't chosen their own. Never the URL —
   *  the whole point is that a person reads words, not an address. */
  defaultLinkText?: string;
}

// ── Sensitive-name denylist. A token whose path matches ANY of these is
// removed from the catalog before export (and a test asserts none ever
// survive). Substring/word matches on the encrypted+regulated families
// enumerated in CLAUDE.md's SENSITIVE_FIELD rule, plus the structural
// markers (*_encrypted, *_last_four) that would be a value side channel.
const SENSITIVE_TOKEN_PATTERNS: readonly RegExp[] = [
  // Structural markers — a value side channel regardless of field family.
  /_encrypted/,
  /_last_four/,
  // Regulated field families. The distinctive ones (ssn, medicare, …) are
  // matched WITHOUT word boundaries so underscore-joined variants
  // (spouse_ssn, medicare_id) are caught too; the short/ambiguous ones
  // (dl, ein, mbi) keep boundaries to avoid false-positives (vein, handle).
  /ssn/,
  /social_security/,
  /drivers?_?licen[sc]e/,
  /\bdl\b/,
  /bank_account/,
  /account_number/,
  /routing/,
  /account_name/,
  /medicare/,
  /beneficiary_id/,
  /\bmbi\b/,
  /\btax_id\b/,
  /\bein\b/,
  /password/,
];

/** True when a token NAME belongs to an encrypted/regulated field and
 *  must never be offered as a merge token. Exported so the guard test
 *  can assert both directions (catalog is clean; known bad names trip). */
export function isSensitiveTokenName(token: string): boolean {
  const lower = token.toLowerCase();
  return SENSITIVE_TOKEN_PATTERNS.some((re) => re.test(lower));
}

// The raw catalog. Grouped for the picker (Contact / Household / This
// record). Record tokens carry their grain so the picker shows only the
// set matching the rule. NOTHING regulated/encrypted appears here — and
// the export below filters through isSensitiveTokenName as a backstop.
const RAW_CATALOG: readonly MergeTokenDef[] = [
  // ── Contact (the recipient) ──
  // Labeled "First name", not "Preferred name" — it resolves to the
  // preferred name when one is on file and the legal first name
  // otherwise (merge-context-builder.ts). The old label promised a
  // nickname-only field, which is what it actually was until 2026-08-21:
  // 98.8% of contacts have no preferred name, so it rendered as literal
  // braces in a client's inbox. The token PATH is deliberately unchanged
  // so every rule already using it keeps working untouched.
  { token: "contact.preferred_name", group: "contact", label: "First name", sample: "Sam" },
  { token: "contact.legal_first_name", group: "contact", label: "Legal first name", sample: "Samuel" },
  { token: "contact.legal_last_name", group: "contact", label: "Last name", sample: "Sample" },
  // Nate, 2026-09-09: AAR forms caption a "Buyer" / "Seller" / "Printed
  // Name" blank expecting the name on their ID, and full_name doesn't
  // promise that — it's the FRIENDLY name (preferred_name when a
  // contact has one, legal_first_name otherwise), built for "Hi Sam,"
  // in an email. This token is the other thing: legal_first_name +
  // legal_last_name ONLY, no nickname substitution, ever. When the
  // legal name isn't on file it resolves BLANK rather than falling
  // back to full_name — a blank line on a contract costs a few
  // seconds; a nickname printed where a legal signature block expects
  // the name on the signer's ID is a wrong fact that looks deliberate
  // (CLAUDE.md's accuracy rule). See merge-context-builder.ts.
  { token: "contact.legal_full_name", group: "contact", label: "Legal full name", sample: "Samuel Sample" },
  // Sample deliberately shows the NICKNAME case so the picker preview
  // makes the two fields tellable apart at a glance — legal_full_name
  // above always shows "Samuel Sample", full_name here can show "Sam
  // Sample" for a contact who goes by a shorter first name.
  { token: "contact.full_name", group: "contact", label: "Full name (greeting)", sample: "Sam Sample" },
  { token: "contact.email", group: "contact", label: "Email address", sample: "sam@example.com" },
  { token: "contact.phone", group: "contact", label: "Phone number", sample: "(480) 555-0142" },
  { token: "contact.birthday", group: "contact", label: "Birthday", sample: "March 3" },
  { token: "contact.referral_source", group: "contact", label: "Referral source", sample: "Current client" },
  // INTERNAL_ONLY — see MergeTokenDef.internalOnly. Bindable onto a
  // client's own contract until 2026-08-23, which would have printed
  // "Not A Fit" on a document mailed to that household.
  { token: "contact.fit_assessment", group: "contact", label: "Fit assessment", sample: "Ideal Fit", internalOnly: true },

  // ── Household ──
  { token: "household.display_name", group: "household", label: "Household name", sample: "The Sample Household" },
  { token: "household.primary_first_name", group: "household", label: "Primary first name", sample: "Sam" },
  { token: "household.primary_last_name", group: "household", label: "Primary last name", sample: "Sample" },
  { token: "household.city", group: "household", label: "City", sample: "Gilbert" },
  { token: "household.state", group: "household", label: "State", sample: "AZ" },
  { token: "household.mailing_address", group: "household", label: "Mailing address", sample: "5522 E Azara Dr, San Tan Valley, AZ 85140" },
  { token: "household.aum", group: "household", label: "Assets under management", sample: "$540,000" },
  { token: "household.client_since", group: "household", label: "Client since", sample: "May 12, 2021" },

  // ── This record — Transaction (Processing) ──
  { token: "transaction.product_noun", group: "record", grain: "transaction", label: "What it is", sample: "Refinance Mortgage" },
  { token: "transaction.stage", group: "record", grain: "transaction", label: "Stage", sample: "Clear to close" },
  { token: "transaction.amount", group: "record", grain: "transaction", label: "Amount", sample: "$350,000" },
  // REALTY ONLY — this is Stewardship's commission. On a Realty
  // contract it is a required disclosure; anywhere else it is the leak
  // CLAUDE.md forbids. See MergeTokenDef.realtyOnly.
  //
  // NOTE the label. It is deliberately NOT "commission": on a CLOSED
  // Realty deal this figure is BrokerMint's Office NET — what the
  // brokerage keeps after the agent split — while a buyer-broker
  // agreement discloses the total the client agrees to pay. At
  // agreement-signing time (an open deal) it is the 2.5% gross estimate,
  // which is the right ballpark, but the meaning shifts as the deal
  // closes. The precise figures live on the Realty record itself
  // (service_record.commission_rate / .gross_commission below) and
  // should be preferred on anything a client signs.
  { token: "transaction.revenue", group: "record", grain: "transaction", label: "Estimated revenue to Stewardship", sample: "$4,200", realtyOnly: true },
  { token: "transaction.expected_close", group: "record", grain: "transaction", label: "Expected close date", sample: "March 12, 2027" },
  // Nate, 2026-08-11 — the sentence he wants a client to receive:
  // "day 4 of a 10 day inspection period". Resolves to a blank when the
  // record doesn't carry both halves of the period, so a rule can never
  // mail out a half-known day.
  { token: "transaction.inspection_period", group: "record", grain: "transaction", label: "Inspection period status", sample: "Day 4 of 10 · ends August 15, 2026" },

  // ── The negotiated terms — Nate's contract card (2026-08-29) ──
  //
  // Migration 20260825163218 gave the OS one governed home for these —
  // "any screen that keeps its own copy of a contract value is a bug"
  // (Nate's spec) — but nothing could put them ON a document. An AAR
  // Purchase Contract has a real blank for every one of these; without
  // these tokens an agent retypes what the contract card already holds.
  //
  // realtyOnly for the same reason the migration itself gives: "nothing
  // outside the Realty contract card writes them" — these columns exist
  // on `transactions` but are Realty-only in practice, so surfacing them
  // in the company-blind catalog would only clutter every other
  // company's automation-email picker with fields that always resolve
  // blank there.
  { token: "transaction.earnest_money", group: "record", grain: "transaction", label: "Earnest money", sample: "$5,000", realtyOnly: true },
  { token: "transaction.loan_type", group: "record", grain: "transaction", label: "Loan type", sample: "Conventional", realtyOnly: true },
  { token: "transaction.loan_amount", group: "record", grain: "transaction", label: "Loan amount", sample: "$360,000", realtyOnly: true },
  { token: "transaction.possession", group: "record", grain: "transaction", label: "Possession", sample: "At close of escrow", realtyOnly: true },
  { token: "transaction.seller_concessions", group: "record", grain: "transaction", label: "Seller concessions", sample: "$3,500", realtyOnly: true },
  { token: "transaction.other_party_name", group: "record", grain: "transaction", label: "Other party (seller) name", sample: "Robert and Maria Delgado", realtyOnly: true },
  { token: "transaction.repair_credit", group: "record", grain: "transaction", label: "Repair credit", sample: "$1,200", realtyOnly: true },
  // The Commission Disbursement Authorization's credit line (2026-10-27),
  // from the Contract card on the Work tab. Number only, like the listing
  // commission boxes beside it.
  { token: "transaction.commission_credit_amount", group: "record", grain: "transaction", label: "Commission credit $ (number only)", sample: "1,500.00", realtyOnly: true },
  { token: "transaction.commission_credit_to", group: "record", grain: "transaction", label: "Commission credit goes to (Buyer / Seller / N/A)", sample: "Seller", realtyOnly: true },
  { token: "transaction.program_id", group: "record", grain: "transaction", label: "Program ID", sample: "FHA-203B", realtyOnly: true },
  { token: "transaction.cda_sent_to_title", group: "record", grain: "transaction", label: "CDA sent to title", sample: "August 20, 2026", realtyOnly: true },
  // The day the buyer signed the purchase contract (Grant, 2026-10-14):
  // "Many of our forms reference this date." Not acceptance.
  { token: "transaction.contract_dated", group: "record", grain: "transaction", label: "Contract dated (buyer signed)", sample: "October 9, 2026", realtyOnly: true },

  // The escrow/title contact on THIS deal (2026-08-29) — §3a of the
  // Purchase Contract. Fax has no home in the OS (flagged, not invented).
  { token: "transaction.escrow_company_name", group: "record", grain: "transaction", label: "Escrow / title company", sample: "First American Title", realtyOnly: true },
  { token: "transaction.escrow_company_email", group: "record", grain: "transaction", label: "Escrow / title email", sample: "escrow@firstam.com", realtyOnly: true },
  // §3a's address and phone (Nate, 2026-11-03), from the title officer's
  // address and phone — see escrow_company_street on the record type.
  { token: "transaction.escrow_company_street", group: "record", grain: "transaction", label: "Escrow / title street address", sample: "1955 E Warner Rd Ste 3", realtyOnly: true },
  { token: "transaction.escrow_company_city", group: "record", grain: "transaction", label: "Escrow / title city", sample: "Tempe", realtyOnly: true },
  { token: "transaction.escrow_company_state", group: "record", grain: "transaction", label: "Escrow / title state", sample: "AZ", realtyOnly: true },
  { token: "transaction.escrow_company_zip", group: "record", grain: "transaction", label: "Escrow / title ZIP", sample: "85284", realtyOnly: true },
  { token: "transaction.escrow_company_phone", group: "record", grain: "transaction", label: "Escrow / title phone", sample: "(480) 555-0301", realtyOnly: true },
  // The officer's own name, and the title company's file number (Nate,
  // 2026-10-26). The officer comes off the contacts tab's escrow/title
  // party; the file number is read off the earnest money receipt (or
  // typed) and kept on the Realty record.
  { token: "transaction.escrow_officer_name", group: "record", grain: "transaction", label: "Escrow officer", sample: "Sarah Zocolo", realtyOnly: true },
  { token: "transaction.escrow_file_number", group: "record", grain: "transaction", label: "Escrow file number", sample: "2026-04711-QC", realtyOnly: true },
  { token: "transaction.people", group: "record", grain: "transaction", label: "Who this is for", sample: "Sam and Sarah Sample" },

  // ── For contract lines that print more than one fact per blank ──
  // (Nate, 2026-09-25)
  //
  // `transaction.people` above is the greeting-card answer: it prefers a
  // nickname and appends "(unconfirmed — check the record)". Line 1a of
  // the AAR purchase contract, "BUYER'S NAME(S)", wants every buyer's
  // LEGAL name on one line. This is that: legal first + legal last for
  // each person the record is for, joined with "and", no nickname and no
  // marker. If any one person is missing a legal name it resolves blank,
  // so the send stops and names the line rather than dropping a buyer.
  //
  // And the close-of-escrow line is three boxes (month / day / 20__), so
  // the one date is also offered in its parts. See shared/realty/date-parts.ts.
  { token: "transaction.people_legal_names", group: "record", grain: "transaction", label: "Everyone's legal names, one line", sample: "Samuel Sample and Sarah Sample", realtyOnly: true },
  { token: "transaction.expected_close_month", group: "record", grain: "transaction", label: "Close date: month", sample: "March", realtyOnly: true },
  { token: "transaction.expected_close_day", group: "record", grain: "transaction", label: "Close date: day", sample: "12", realtyOnly: true },
  { token: "transaction.expected_close_year_short", group: "record", grain: "transaction", label: "Close date: year (after \"20\")", sample: "27", realtyOnly: true },

  // ── This record — Opportunity ──
  { token: "opportunity.product_noun", group: "record", grain: "opportunity", label: "What it is", sample: "Home Policy" },
  { token: "opportunity.stage", group: "record", grain: "opportunity", label: "Stage", sample: "Proposal" },
  { token: "opportunity.amount", group: "record", grain: "opportunity", label: "Amount", sample: "$1,800" },
  // REALTY ONLY — see transaction.revenue above for the label's caveat.
  { token: "opportunity.revenue", group: "record", grain: "opportunity", label: "Estimated revenue to Stewardship", sample: "$900", realtyOnly: true },
  { token: "opportunity.expected_close", group: "record", grain: "opportunity", label: "Expected close date", sample: "April 1, 2027" },
  { token: "opportunity.people", group: "record", grain: "opportunity", label: "Who this is for", sample: "Sam and Sarah Sample" },

  // ── This offer (2026-09-01) ──
  //
  // Generated from the offer-slot catalog rather than listed by hand:
  // that catalog already declares every slot's label, example and
  // token, and two hand-maintained lists of the same thing drift. A
  // slot added there appears here automatically. (This is the file's
  // one import, and it is to another dependency-free pure module.)
  //
  // Realty-only for the picker, and opportunity-grain: these resolve
  // from the offer summary attached to the opportunity a document is
  // sent from. On any other record they resolve blank — and the
  // send-time guard REFUSES a bound field that resolves blank, so a
  // stray offer binding on a mortgage document stops the send rather
  // than printing an empty line onto a contract.
  ...OFFER_SLOTS.map(
    (slot): MergeTokenDef => ({
      token: slot.token,
      group: "record",
      grain: "opportunity",
      label: slot.label,
      sample: slot.example,
      realtyOnly: true,
    }),
  ),

  // ── This record — Service record (policy / loan / case / …) ──
  { token: "service_record.product_noun", group: "record", grain: "service_record", label: "What it is", sample: "Home Policy" },
  { token: "service_record.status", group: "record", grain: "service_record", label: "Status", sample: "Active" },
  { token: "service_record.effective_date", group: "record", grain: "service_record", label: "Effective date", sample: "March 3, 2026" },
  { token: "service_record.renewal_date", group: "record", grain: "service_record", label: "Renewal / end date", sample: "March 3, 2027" },
  { token: "service_record.amount", group: "record", grain: "service_record", label: "Premium / amount", sample: "$1,800" },
  // Resolves to the carrier's CURRENT brand (loadServiceRecord in
  // merge-context-builder.ts runs it through carrierCurrentBrand), not
  // necessarily the name on file — "Safeco" merges as "Liberty Mutual".
  { token: "service_record.carrier", group: "record", grain: "service_record", label: "Carrier", sample: "Travelers" },
  { token: "service_record.policy_number", group: "record", grain: "service_record", label: "Policy number", sample: "HO-4482119" },
  { token: "service_record.loan_number", group: "record", grain: "service_record", label: "Loan number", sample: "LN-2026-0184" },
  // Medicare/health Planning cases only (Andrea, 2026-08-31 — her OEP
  // requote rule wanted "your PDP" or "your MAPD" plan, and the picker
  // had no way to say which). Blank on every other service-record
  // family, same as loan_number is blank on a policy.
  { token: "service_record.plan_type", group: "record", grain: "service_record", label: "Plan type (Medicare)", sample: "MAPD" },
  // The single field a real estate or mortgage document most needs
  // (2026-08-23). Resolved from the loan, the listing or the purchase —
  // whichever this record is — and blank rather than partial when the
  // street is missing, so the send-time guard refuses it instead of
  // printing "Gilbert, AZ" on a contract with no address.
  { token: "service_record.property_address", group: "record", grain: "service_record", label: "Property address", sample: "1234 N Palm Ave, Gilbert, AZ 85234" },
  // The same address in pieces, for forms that print street, city and
  // zip in separate boxes (Nate, 2026-10-15). Binding the whole address
  // into a "City" box printed the street there too.
  { token: "service_record.property_street", group: "record", grain: "service_record", label: "Property street address", sample: "1234 N Palm Ave" },
  { token: "service_record.property_city", group: "record", grain: "service_record", label: "Property city", sample: "Gilbert" },
  { token: "service_record.property_zip", group: "record", grain: "service_record", label: "Property ZIP code", sample: "85234" },
  // Realty commission, for the documents that must state it. These are
  // the figures a buyer-broker agreement and a purchase contract
  // actually disclose — the RATE the client agrees to, and the TOTAL
  // commission on the deal — as opposed to "Estimated revenue to
  // Stewardship", which becomes our post-split take once the deal
  // closes. Preferred on anything a client signs, for that reason.
  //
  // Realty-only in the same sense as the revenue tokens: they resolve
  // from the realty subtype tables, so they are simply blank on a policy
  // or a loan, and they are kept out of the company-blind static catalog.
  { token: "service_record.commission_rate", group: "record", grain: "service_record", label: "Commission rate", sample: "2.50%", realtyOnly: true },
  { token: "service_record.gross_commission", group: "record", grain: "service_record", label: "Total commission on the sale", sample: "$11,250", realtyOnly: true },
  // Our listing-side commission in the two boxes a disbursement form
  // splits it across (Nate, 2026-10-26: "Listing Agent Commission: % ___ =
  // $ ___"). Number only, because the form prints the % and the $. The
  // rate is the listing's own, else the firm's 1.98% default; the amount
  // is the flat dollars if one was typed, else rate x sale price (asking
  // price before there is a sale). Listings only.
  { token: "service_record.listing_commission_pct", group: "record", grain: "service_record", label: "Listing commission % (number only)", sample: "1.98", realtyOnly: true },
  { token: "service_record.listing_commission_amount", group: "record", grain: "service_record", label: "Listing commission $ (number only)", sample: "10,890.00", realtyOnly: true },
  // The buyer's agent's side, the next line on the same form (2026-10-27).
  { token: "service_record.buyer_agent_commission_pct", group: "record", grain: "service_record", label: "Buyer's agent commission % (number only)", sample: "3", realtyOnly: true },
  { token: "service_record.buyer_agent_commission_amount", group: "record", grain: "service_record", label: "Buyer's agent commission $ (number only)", sample: "16,500.00", realtyOnly: true },

  // ── The property's identity, for the contracts that name it ──
  //
  // (2026-08-29.) A real estate contract does not just say WHERE the
  // property is — it says WHICH property it is, legally: the AAR
  // Purchase Contract §1b and the Listing Contract §2 both carry an
  // Assessor's # blank, a county blank, and a legal-description blank
  // copied character for character off the title commitment. The OS
  // started storing all of these on 2026-08-28 (realty_property_facts),
  // and without these tokens an agent would retype what the OS already
  // knows — the BrokerMint complaint, reproduced.
  //
  // In the ordinary catalog rather than realtyOnly, alongside
  // property_address which they belong with: a parcel number is public
  // county-assessor data, not a compensation disclosure, so the reason
  // that keeps the commission tokens out does not apply. They resolve
  // blank on a record that has no property, the same way.
  { token: "service_record.parcel_number", group: "record", grain: "service_record", label: "Assessor's parcel number", sample: "123-45-678" },
  { token: "service_record.county", group: "record", grain: "service_record", label: "County", sample: "Maricopa" },
  { token: "service_record.legal_description", group: "record", grain: "service_record", label: "Legal description", sample: "Lot 42, PALM GROVE UNIT 3, Book 512 Page 18" },
  { token: "service_record.year_built", group: "record", grain: "service_record", label: "Year built", sample: "1974" },

  // The representation agreement's own dates — the Listing Contract's
  // §3 commencement/expiration on a listing, the Buyer-Broker
  // Agreement's on a purchase. One pair of tokens serves both sides
  // because the two subtype tables name the same thing differently
  // (listing_agreement_* / buyer_representation_*) and the read layer
  // already unifies them.
  { token: "service_record.agreement_signed_date", group: "record", grain: "service_record", label: "Agreement signed date", sample: "March 3, 2026" },
  { token: "service_record.agreement_expiration_date", group: "record", grain: "service_record", label: "Agreement expiration date", sample: "September 3, 2026" },

  // The listing agreement's own terms (Grant, 2026-10-14) — the Contract
  // card's Listing Contract section. Listing side only; blank (never a
  // hole) on a purchase. realtyOnly because nothing outside a Realty
  // listing has them.
  { token: "service_record.listing_period_start_date", group: "record", grain: "service_record", label: "Listing period start", sample: "March 3, 2026", realtyOnly: true },
  { token: "service_record.original_list_price", group: "record", grain: "service_record", label: "Original list price", sample: "$615,000", realtyOnly: true },
  { token: "service_record.leased_items", group: "record", grain: "service_record", label: "Leased items", sample: "Solar: Sunrun lease", realtyOnly: true },
  { token: "service_record.items_not_conveying", group: "record", grain: "service_record", label: "Items not conveying", sample: "Garage refrigerator; dining room chandelier", realtyOnly: true },

  // Listing Contract §4: the extra compensation owed when Stewardship's
  // own agent also finds the buyer. There is no per-listing column for
  // it — the firm sets one number (Nate, 2026-08-29: 2.00%) and it lives
  // in realty_listing_contract_defaults, so this token reads the policy
  // in force on the day the document is sent.
  //
  // realtyOnly for the same reason commission_rate is: it is a
  // compensation figure Arizona practice requires ON the listing
  // contract and nowhere else.
  { token: "service_record.unrepresented_buyer_rate", group: "record", grain: "service_record", label: "Additional rate if we find the buyer", sample: "2.00%", realtyOnly: true },
  { token: "service_record.people", group: "record", grain: "service_record", label: "Who this is for", sample: "Sam Sample" },

  // ── This record — Ongoing Care (the care plan itself) ──
  // Offered alongside the service-record tokens above on a care-plan
  // rule, because the plan's linked policy/loan/case is resolved with it.
  { token: "care_plan.name", group: "record", grain: "rhythm_instance", label: "Care plan name", sample: "Renewal Plan" },
  { token: "care_plan.stage", group: "record", grain: "rhythm_instance", label: "Care plan stage", sample: "Stay Recommended" },
  { token: "care_plan.cycle_start", group: "record", grain: "rhythm_instance", label: "Cycle start", sample: "March 3, 2026" },
  { token: "care_plan.cycle_end", group: "record", grain: "rhythm_instance", label: "Cycle end", sample: "March 2, 2027" },

  // ── Form — a link the recipient can act on, minted fresh per send ──
  // Unlike every other token above, this one has a SIDE EFFECT: using it
  // creates a fresh secure intake-form instance for the household at
  // send time (automation-engine.ts), the same kind the household page
  // mints by hand. It's deliberately narrow (the one insurance renewal
  // form) rather than a generic "any form" picker — that generalization
  // is Forms Platform phase F4, sequenced separately.
  {
    token: "form.renewal_intake_link",
    group: "form",
    label: "Renewal intake form link",
    sample: "https://os.stewardship.pro/intake/r/sample-token-a1b2c3",
    isLink: true,
    defaultLinkText: "Open your renewal form",
  },
  // The generic form link (Forms Platform F4, the generalization the
  // token above was deliberately deferring). Which form it points at is
  // the rule's `form_slug` config, so one token serves every form
  // instead of a new hardcoded token per form. Same side effect: using
  // it mints a fresh personal link for THIS recipient's household at
  // send time, and the link is what binds the submission back to them —
  // no "please use the same email you gave us" instruction, and nothing
  // to mis-match.
  // Who on a returned questionnaire still needs a human (2026-09-03).
  //
  // This is the ONE token that resolves to a short CLAUSE rather than a
  // bare value, and the reason is that its common case is nothing at all:
  // when everybody confirmed, an author writing "Still to confirm:
  // {{names}}" would be left with a dangling label on most tasks. A
  // clause that disappears cleanly is the only shape that reads right in
  // both states. The wording around it stays the author's — this is an
  // optional addition to a sentence they wrote, not a message composed in
  // code — and it resolves to "" on every form without per-person
  // sections, which is the honest answer there.
  {
    token: "form.follow_up_note",
    group: "form",
    label: "Who still needs follow-up (blank when everyone confirmed)",
    sample: "Linda still needs to confirm their information.",
  },
  {
    token: "form.link",
    group: "form",
    label: "Form link",
    isLink: true,
    // Overridable per rule via the email action's "Link text" field; this
    // is only what an author who never touched that field gets.
    defaultLinkText: "Open the form",
    // The forms front door, matching what the engine actually mints
    // (2026-08-31). The old sample named the OS host and so made the
    // wrong origin look deliberate in review.
    sample: "https://forms.stewardship.pro/f/t/sample-token-a1b2c3",
  },

  // The personal booking link (native scheduling Phase C). Side effect
  // like the intake link above: using it mints a fresh booking invite
  // for the recipient at send time (automation-engine.ts), opening the
  // zero-typing /book/i page that greets them by first name and books
  // with the rule's owner.
  {
    token: "booking.link",
    group: "booking",
    label: "Personal booking link",
    sample: "https://os.stewardship.pro/book/i/sample-token-a1b2c3",
    isLink: true,
    defaultLinkText: "Grab a time",
  },

  // The recipient's next upcoming appointment (native scheduling Phase
  // C2) — resolved at send time by automation-engine.ts (a pure read,
  // unlike booking.link's mint). Built for the "appointment is coming
  // up" reminder trigger, but any automation email may reference them;
  // a recipient with nothing booked makes the send a visible skip,
  // never a blank (same honesty rule as the links above).
  {
    token: "appointment.when",
    group: "appointment",
    label: "Appointment day & time",
    sample: "Friday, August 22 at 2:00 PM Arizona time",
  },
  {
    token: "appointment.advisor",
    group: "appointment",
    label: "Who the appointment is with",
    sample: "Joy Smith",
  },
  {
    token: "appointment.type",
    group: "appointment",
    label: "Appointment type",
    sample: "Renewal review",
  },
  // Side effect like booking.link: using it mints a FRESH manage key
  // for the recipient's upcoming appointment at send time
  // (automation-engine.ts), so each reminder carries its own working
  // reschedule/cancel link and the confirmation email's original link
  // stays valid alongside it.
  {
    token: "appointment.manage_link",
    group: "appointment",
    label: "Reschedule / cancel link",
    sample: "https://os.stewardship.pro/book/manage/sample-token-a1b2c3",
  },

  // The document a client just signed (Grant, 2026-08-23: "the client
  // needs an email confirmation with the document they signed"). Both
  // resolve ONLY on the "a document is signed" trigger, from that
  // event's own payload — everywhere else the send is a visible skip
  // rather than an email with a blank where a document should be.
  //
  // The link is a side effect like booking.link: using it mints a FRESH
  // copy link for THIS recipient, so the one they already have from the
  // completion page keeps working alongside it.
  // What the county recorder shows about a house we financed (win-back,
  // Grant 2026-09-19/22). Resolve ONLY on "the county shows a refinance
  // with another lender"; lender names are institutions, never people.
  { token: "county.new_lender", group: "county", label: "Their new lender", sample: "ROCKET MORTGAGE LLC" },
  { token: "county.recorded_month", group: "county", label: "When their new loan was recorded", sample: "Aug 2026" },
  { token: "county.new_amount", group: "county", label: "Their new loan amount", sample: "$512,000" },
  { token: "county.new_loan_type", group: "county", label: "Their new loan type", sample: "conventional" },
  { token: "county.our_amount", group: "county", label: "Our loan amount", sample: "$486,000" },
  { token: "county.our_funded_year", group: "county", label: "Year our loan funded", sample: "2022" },
  { token: "county.other_services", group: "county", label: "Other Stewardship services they have", sample: "Insurance" },
  { token: "county.as_of", group: "county", label: "County record as of", sample: "Sep 12, 2026" },

  {
    token: "document.name",
    group: "document",
    label: "Signed document name",
    sample: "Listing Agreement (signed).pdf",
  },
  {
    token: "document.link",
    group: "document",
    label: "Link to their signed copy",
    sample: "https://os.stewardship.pro/sign/sample-token-a1b2c3",
  },

  // ── The send itself (Baker, 2026-09-16) ──
  //
  // "This really needs to be a locked text box that populates the date
  // the document is sent for signature, but that is not a field that I
  // can pull from in the locked text box pull from record feature."
  // It wasn't. Now it is, and it is the only token in its group.
  {
    token: "envelope.date_sent",
    group: "envelope",
    label: "Date this is sent for signature",
    sample: "September 16, 2026",
  },
  // Nate, 2026-10-21: the Buyer-Broker agreement expires six months
  // after it starts. When the start date is typed over on the prep
  // screen this follows it (shared/esign/field-merge-bindings.ts).
  {
    token: "envelope.date_sent_plus_six_months",
    group: "envelope",
    label: "Six months after the send date",
    sample: "March 16, 2027",
  },

  // ── Whoever signs this box (Baker, 2026-10-03) ──
  //
  // "I would love for it to auto populate the name when I actually go to
  // send the document out." The Realty team's annual policy statement
  // goes to team members, who are not contacts, so no `contact.` field
  // could ever fill it. This reads the name the send is addressed to.
  {
    token: "signer.name",
    group: "signer",
    label: "Signer's name (as it is on the send)",
    sample: "Jordan Taylor",
  },

  // Advisor tokens (advisor.name / advisor.email) describe the SENDER,
  // not the recipient, so they aren't part of the grouped personalization
  // picker — they're added to KNOWN_MERGE_TOKENS below so the resolver
  // still fills them and treats them as known (blank-on-absent).

  // ── The sending advisor's own license (2026-08-29) ──
  //
  // Unlike advisor.name/email, this genuinely belongs in a picker: a
  // license number appears nowhere else on the Prepare & Send screen
  // (no signature line, no sender chip shows it), so it can only reach
  // a document by being placed as a field. `realtyOnly` + `group:
  // "advisor"` together mean it's offered ONLY by the e-signature
  // picker on a Realty send (esign.ts adds "advisor" to its
  // OFFERED_GROUPS; the automation email editor's own group allow-list
  // does NOT include "advisor", so a marketing email never offers it).
  // The agent's own name and email, offered on a Realty contract
  // (Nate, 2026-10-15: "my information as an agent should be able to be
  // automatically applied to lines 442-447"). They always RESOLVED (see
  // ADVISOR_TOKENS); they were simply never offered, so the purchase
  // contract's "PRINT AGENT'S NAME" and "EMAIL" lines stayed unbound and
  // he typed himself in on every offer. Realty-only like the licence
  // tokens beside them, so no email editor starts offering them.
  { token: "advisor.name", group: "advisor", label: "Your name", sample: "Nathaniel Swartz", realtyOnly: true },
  { token: "advisor.email", group: "advisor", label: "Your email", sample: "nate@stewardship.pro", realtyOnly: true },
  { token: "advisor.license_number", group: "advisor", label: "Your real-estate license #", sample: "SA123456000", realtyOnly: true },
  { token: "advisor.license_state", group: "advisor", label: "License state", sample: "AZ", realtyOnly: true },
  // The agent's own MLS code, beside the licence number the table already
  // holds (Nate ns471, Mark ms432). Every agent's offers default to their
  // own code — Nate, 2026-09-14.
  { token: "advisor.mls_code", group: "advisor", label: "Your MLS code", sample: "ns471", realtyOnly: true },
  // The number they want PRINTED on a contract, which is not always the
  // one in their user profile.
  { token: "advisor.contract_phone", group: "advisor", label: "Your phone, as it prints on a contract", sample: OFFICE_PHONE_DISPLAY, realtyOnly: true },

  // ── The firm's own block (2026-09-16) ──
  //
  // Every AAR contract asks who the broker is, near the signatures: firm
  // name, MLS code, state licence, address, fax. The atlas recorded these
  // as coming from a `realty_firm_profile` table that did not exist, so
  // they printed empty on the signature page. They now read one governed
  // row per company (`firm_profiles`).
  //
  // NOT realtyOnly: the Loan Status Update and the Pre-Qualification form
  // ask Stewardship Mortgage the same questions in NMLS words, and the
  // one row answers both. A company whose row is empty resolves blank,
  // which is exactly what prints today.
  { token: "firm.name", group: "firm", label: "Firm legal name", sample: "Stewardship Realty LLC" },
  { token: "firm.street", group: "firm", label: "Firm street address", sample: "1094 S Gilbert Rd Ste 210" },
  // One blank, two facts: AAR line 446 prints street and city together
  // and gives STATE and ZIP their own boxes beside it.
  { token: "firm.street_and_city", group: "firm", label: "Firm street and city", sample: "1094 S Gilbert Rd Ste 210, Gilbert" },
  { token: "firm.city", group: "firm", label: "Firm city", sample: "Gilbert" },
  { token: "firm.state", group: "firm", label: "Firm state", sample: "AZ" },
  { token: "firm.postal_code", group: "firm", label: "Firm ZIP", sample: "85296" },
  { token: "firm.phone", group: "firm", label: "Firm phone", sample: OFFICE_PHONE_DISPLAY },
  { token: "firm.fax", group: "firm", label: "Firm fax", sample: "602-428-9906" },
  { token: "firm.email", group: "firm", label: "Firm email", sample: OFFICE_EMAIL },
  { token: "firm.license_number", group: "firm", label: "Firm state license #", sample: "LC704827000" },
  { token: "firm.industry_code", group: "firm", label: "Firm MLS code / NMLS #", sample: "stewr001" },

  // ── The firm's standing answers on a purchase contract ──
  //
  // A governed policy row (realty_offer_defaults), not a per-deal value:
  // the firm answers once and every offer starts there, with the agent
  // free to override on the contract. The listing side has had its twin
  // since 2026-08-29; these are the buy-side blanks Nate corrected in his
  // review, which printed empty until the table existed.
  //
  // realtyOnly: an AAR purchase contract asks these questions and no
  // other company's document does.
  //
  // Only the blanks that print TEXT are here. The rest of that policy row
  // decides which CHECKBOX is ticked (earnest money by personal check,
  // the appraisal fee payer, the 8q agency election), and the OS has no
  // settled way to express a pre-ticked box yet — see the note in
  // shared/esign/atlas-import.ts. Those ride in the fill rule until it
  // does, rather than being faked as a printed word.
  { token: "offer_default.seller_response_days", group: "offer_default", label: "Seller responds within (days)", sample: "5", realtyOnly: true },
  { token: "offer_default.receipts_days_prior_coe", group: "offer_default", label: "Receipts due before closing (days)", sample: "3", realtyOnly: true },
  { token: "offer_default.buyer_broker_geographic_area", group: "offer_default", label: "Buyer-broker geographic area", sample: "Maricopa and Pinal County", realtyOnly: true },
  { token: "offer_default.home_warranty_placeholder", group: "offer_default", label: "Home warranty, when none named", sample: "TBD", realtyOnly: true },

  // ── The agent across the table (2026-09-16) ──
  //
  // The seller's half of a purchase contract's signature page, and the
  // buyer's half of a listing's. Off the party roster, because that row
  // is already the OS's answer to "who else is on this deal, in what
  // role". realtyOnly: no other company's forms name an opposing agent.
  { token: "other_agent.name", group: "other_agent", label: "Other agent's name", sample: "Karen Ortiz", realtyOnly: true },
  { token: "other_agent.email", group: "other_agent", label: "Other agent's email", sample: "kortiz@westusa.com", realtyOnly: true },
  { token: "other_agent.phone", group: "other_agent", label: "Other agent's phone", sample: "(480) 555-0142", realtyOnly: true },
  { token: "other_agent.license_number", group: "other_agent", label: "Other agent's license #", sample: "SA664333000", realtyOnly: true },
  { token: "other_agent.mls_code", group: "other_agent", label: "Other agent's MLS code", sample: "kort22", realtyOnly: true },
  { token: "other_agent.firm_name", group: "other_agent", label: "Their brokerage", sample: "West USA Realty", realtyOnly: true },
  { token: "other_agent.firm_mls_code", group: "other_agent", label: "Their brokerage's MLS code", sample: "wusa01", realtyOnly: true },
  { token: "other_agent.firm_license_number", group: "other_agent", label: "Their brokerage's license #", sample: "LC506032000", realtyOnly: true },
];

/** The exported catalog — what any picker may OFFER. Every entry is
 *  structurally guaranteed non-sensitive by the isSensitiveTokenName
 *  backstop, and carries no INTERNAL_ONLY value (see below). */
export const MERGE_FIELD_CATALOG: readonly MergeTokenDef[] = RAW_CATALOG.filter(
  (t) => !isSensitiveTokenName(t.token) && !t.internalOnly && !t.realtyOnly,
);

/** The INTERNAL_ONLY paths, kept addressable on purpose.
 *
 *  They are gone from the catalog, so nothing can offer them. But they
 *  stay in KNOWN_MERGE_TOKENS below, which is what decides blank-vs-
 *  literal in the resolver — so a rule written before 2026-08-23 that
 *  already references one now renders BLANK rather than printing
 *  "{{contact.fit_assessment}}" in a client's inbox. Blank is the right
 *  degradation: the value is gone either way, and braces in front of a
 *  client are their own small embarrassment.
 *
 *  The values are independently absent from the merge context (see
 *  merge-context-builder.ts), so this is a second lock on a door that
 *  is already bolted, exactly as the sensitive families are handled. */
// "Type" is deliberately NOT offered alongside "What it is": both
// resolve to the same product noun, and two rows showing an identical
// value is the exact failure mode a recognition-first picker must
// avoid (staging, 2026-08-23). It stays resolvable for rules written
// before then — see LEGACY_ALIAS_TOKENS.
const LEGACY_ALIAS_TOKENS = [
  "transaction.type",
  "opportunity.type",
  "service_record.type",
] as const;

/** Tokens a REALTY record may disclose and no other company may.
 *
 *  Not in MERGE_FIELD_CATALOG (that catalog is company-blind), so the
 *  e-signature picker adds these itself once it knows the record is
 *  Realty. Known, so they resolve rather than printing braces. */
export const REALTY_ONLY_MERGE_TOKENS: readonly string[] = RAW_CATALOG.filter(
  (t) => t.realtyOnly && !isSensitiveTokenName(t.token),
).map((t) => t.token);

/** The full definitions, for the picker that is allowed to offer them. */
export const REALTY_ONLY_MERGE_FIELDS: readonly MergeTokenDef[] = RAW_CATALOG.filter(
  (t) => t.realtyOnly && !isSensitiveTokenName(t.token),
);

export const INTERNAL_ONLY_MERGE_TOKENS: readonly string[] = RAW_CATALOG.filter(
  (t) => t.internalOnly && !isSensitiveTokenName(t.token),
).map((t) => t.token);

/**
 * What a BLANK FORM — not yet attached to any household — can offer to
 * bind a field to.
 *
 * 2026-09-01, mapping the AAR catalog straight from the Forms Library:
 * every other merge-field lookup in the OS answers "what can THIS
 * RECORD fill in", because a real record is always in hand. A blank
 * template has no record — Nate is mapping the Listing Contract before
 * any listing exists — so this answers a narrower, form-shaped
 * question instead: what could a document of this SHAPE ever need?
 * AAR forms bind at two grains only (a policy/loan/case/listing via
 * `service_record`, or a Processing record's own negotiated terms via
 * `transaction`), so both are offered together rather than making the
 * mapper pre-declare which one a form will be sent from — the send
 * path narrows to whichever grain the real record turns out to be
 * (see `unfilledBindings` in shared/esign/field-merge-bindings.ts,
 * which blocks a send with a field that can't resolve, exactly as it
 * would for a hand-picked binding).
 *
 * Deliberately NOT the same function `esign.ts`'s `offeredMergeTokens`
 * uses for a real record — that one is grain-EXACT because it has a
 * real kind to filter to, and reusing it here would mean teaching it a
 * "no kind yet" case inside code that also gates real, sendable
 * envelopes. Two small, separately-readable functions cost nothing;
 * one function serving both a blank form's preview and a live send's
 * authorization is the kind of shared path where a template-mode bug
 * could leak into the send screen.
 */
export function templateOfferableMergeTokens(
  companySlug: string | null,
): readonly MergeTokenDef[] {
  // "signer" (2026-10-03): the name on the send is what a blank form's
  // name box usually wants, and it is the only name a Productions form
  // can fill at all.
  const OFFERED_GROUPS = new Set(["contact", "household", "record", "advisor", "signer"]);
  const RECORD_GRAINS = new Set(["service_record", "transaction"]);
  const candidates =
    companySlug === "realty"
      ? [...MERGE_FIELD_CATALOG, ...REALTY_ONLY_MERGE_FIELDS]
      : MERGE_FIELD_CATALOG;
  return candidates.filter((t) => {
    if (!OFFERED_GROUPS.has(t.group)) return false;
    if (!groupFillableForCompany(t.group, companySlug)) return false;
    if (t.group !== "record") return true;
    return t.grain !== undefined && RECORD_GRAINS.has(t.grain);
  });
}

/**
 * Token groups that can never fill on a Stewardship Productions send.
 *
 * Productions paperwork goes to team members and new hires, who sign as
 * named signers and are never contacts, and it is filed on a personnel
 * file rather than a household (shared/esign/sendable-kinds.ts). So a
 * `contact.*` or `household.*` field there has nothing to read, and the
 * send refuses it every time. Baker, 2026-10-03, bound "Legal full name"
 * on the Realty team's annual policy statement and would have hit that
 * refusal on every send. Not offering them is the fix; `signer.name` is
 * the name such a form wants.
 */
export const PERSONNEL_UNFILLABLE_GROUPS: ReadonlySet<MergeTokenGroup> = new Set<MergeTokenGroup>([
  "contact",
  "household",
]);

/** May a token of this group be offered on a document for this company?
 *  The one place the Productions rule above is applied, so the blank-form
 *  picker and the real send's picker cannot disagree. */
export function groupFillableForCompany(
  group: MergeTokenGroup,
  companySlug: string | null,
): boolean {
  return companySlug !== PRODUCTIONS_SLUG || !PERSONNEL_UNFILLABLE_GROUPS.has(group);
}

// Advisor tokens the resolver understands but the grouped picker doesn't
// surface as personalization (they describe the SENDER). Listed here so
// they count as "known" (blank-on-absent, never literal) and so
// KNOWN_MERGE_TOKENS stays a superset the send path fills.
const ADVISOR_TOKENS = ["advisor.name", "advisor.email"] as const;

/** Every token that resolves to a URL, mapped to its fallback anchor text.
 *  Built from the catalog so marking a new link token is a one-line change
 *  in its definition rather than an edit in two places that can drift. */
export const LINK_MERGE_TOKEN_DEFAULTS: Readonly<Record<string, string>> =
  Object.freeze(
    Object.fromEntries(
      RAW_CATALOG.filter((t) => t.isLink).map((t) => [
        t.token,
        t.defaultLinkText ?? t.label,
      ]),
    ),
  );

/**
 * The token -> anchor-text map to hand `applyMergeFields` for an HTML body.
 *
 * `authorLinkText` is the rule author's own wording for the generic form
 * link (the email action's "Link text" field); `bookingLinkText` is the
 * wording for the personal booking link (its own field since 2026-09-04,
 * so "Book a time with Brian" and "Fill out your review form" can share
 * one email without sharing one label). Each applies to its own token
 * only; the renewal link keeps its catalog default, and a blank field
 * means the catalog default for that link — what every earlier rule had.
 *
 * Pure + exported so the builder's preview and the send path can't drift.
 */
export function linkTextForBody(
  authorLinkText?: string | null,
  bookingLinkText?: string | null,
): Readonly<Record<string, string>> {
  const chosen = (authorLinkText ?? "").trim();
  const booking = (bookingLinkText ?? "").trim();
  if (!chosen && !booking) return LINK_MERGE_TOKEN_DEFAULTS;
  return {
    ...LINK_MERGE_TOKEN_DEFAULTS,
    ...(chosen ? { "form.link": chosen } : {}),
    ...(booking ? { "booking.link": booking } : {}),
  };
}

// All known merge tokens — the catalog paths plus advisor tokens.
// Exported (readonly string[]) for back-compat with the picker helper.
export const KNOWN_MERGE_TOKENS: readonly string[] = [
  ...MERGE_FIELD_CATALOG.map((t) => t.token),
  // Known-but-never-offered — see INTERNAL_ONLY_MERGE_TOKENS.
  ...INTERNAL_ONLY_MERGE_TOKENS,
  // Known, and offered only by the Realty-aware picker.
  ...REALTY_ONLY_MERGE_TOKENS,
  ...LEGACY_ALIAS_TOKENS,
  // advisor.name / advisor.email are now ALSO Realty-only catalog entries
  // (2026-10-15), so skip any already listed rather than naming a token
  // twice.
  ...ADVISOR_TOKENS.filter((t) => !REALTY_ONLY_MERGE_TOKENS.includes(t)),
];

/** Sample value for EVERY known token, for the test-send preview.
 *
 *  Built from the raw catalog, so it covers the tokens the offered
 *  catalog deliberately hides: the internal-only ones (sample "", because
 *  that is exactly what they render in production) and the legacy
 *  aliases (their real sample — they still resolve). A preview built
 *  from the offered catalog alone would show a literal {{token}} for
 *  those and misrepresent what a client would actually receive. */
export const MERGE_TOKEN_SAMPLES: ReadonlyMap<string, string> = new Map<string, string>([
  ...RAW_CATALOG.filter((t) => !isSensitiveTokenName(t.token)).map(
    (t) => [t.token, t.internalOnly ? "" : t.sample] as [string, string],
  ),
  // The legacy aliases have no catalog row of their own any more, but
  // they still RESOLVE — so the preview needs their sample too, or a
  // rule that uses one shows literal braces in a test send that it
  // would never show a client.
  ["transaction.type", "Refinance Mortgage"],
  ["opportunity.type", "Home Policy"],
  ["service_record.type", "Home Policy"],
]);

/** Blank every INTERNAL_ONLY leaf whose namespace is present.
 *
 *  Without this, an old rule naming {{contact.fit_assessment}} renders
 *  the LITERAL braces to a client, not a blank: the resolver only blanks
 *  a token when its whole namespace is absent, and `contact` is always
 *  present. Writing an explicit empty string is what actually delivers
 *  the "renders blank, never braces" promise — a test-send preview
 *  caught the difference (2026-08-23). */
export function blankInternalOnlyTokens(context: MergeContext): MergeContext {
  for (const token of INTERNAL_ONLY_MERGE_TOKENS) {
    const [ns, leaf] = token.split(".");
    if (!ns || !leaf) continue;
    const bucket = (context as unknown as Record<string, Record<string, unknown> | undefined>)[ns];
    if (bucket && typeof bucket === "object") bucket[leaf] = "";
  }
  return context;
}

/** O(1) membership test the resolver uses to decide blank-vs-literal. */
export const KNOWN_TOKEN_SET: ReadonlySet<string> = new Set(KNOWN_MERGE_TOKENS);

const TOKEN_LABELS: ReadonlyMap<string, string> = new Map(
  MERGE_FIELD_CATALOG.map((t) => [t.token, t.label]),
);

/** The picker's plain-English label for a token path ("First name"), or
 *  undefined for a path that isn't in the catalog (a typo, an advisor
 *  token). Used where a token must be shown to a HUMAN rather than
 *  resolved — e.g. the unfilled-field notice on an Outlook draft. */
export function mergeTokenLabel(token: string): string | undefined {
  return TOKEN_LABELS.get(token);
}
